import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppWindow, NotebookPen, ArrowRight, Sparkles } from "lucide-react";
import { api } from "../lib/api";

const APP_ICONS = { "notebook-pen": NotebookPen };

export default function AppsDirectory() {
  const [apps, setApps] = useState([]);

  useEffect(() => {
    api.get("/apps").then(({ data }) => setApps(data.apps || [])).catch(() => {});
  }, []);

  return (
    <div className="container-narrow py-12 md:py-20" data-testid="apps-directory">
      <div className="text-center mb-12">
        <div className="label-eyebrow mb-3 inline-flex items-center gap-2"><AppWindow className="w-3 h-3" /> Durga apps</div>
        <h1 className="font-heading text-4xl md:text-6xl font-semibold leading-tight">
          Tools for the <span className="italic text-marigold">whole road trip.</span>
        </h1>
        <p className="text-ink2 mt-4 max-w-2xl mx-auto">
          A growing set of software applications built into Durga Programming Adventures. Learn the concept in a lesson, then reach for the tool that turns it into work.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-5" data-testid="apps-grid">
        {apps.map((app) => {
          const Icon = APP_ICONS[app.icon] || AppWindow;
          return (
            <Link
              key={app.slug}
              to={app.route}
              className="card-soft p-8 group hover:-translate-y-0.5 transition"
              data-testid={`apps-card-${app.slug}`}
            >
              <div className="flex items-start gap-5">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${app.accent || "#2A9D8F"}22`, color: app.accent || "#2A9D8F" }}
                >
                  <Icon className="w-7 h-7" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading text-2xl font-semibold">{app.name}</span>
                    {app.status && (
                      <span className="text-[10px] uppercase tracking-widest font-bold text-marigold bg-marigold/10 px-2 py-0.5 rounded-full">{app.status}</span>
                    )}
                  </div>
                  <p className="text-sm italic text-ink2 mt-1">{app.tagline}</p>
                  <p className="text-ink2 mt-3 leading-relaxed">{app.description}</p>
                  {app.capabilities?.length > 0 && (
                    <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-ink2">
                      {app.capabilities.slice(0, 6).map((c, i) => (
                        <li key={i} className="inline-flex items-center gap-1.5">
                          <Sparkles className="w-3 h-3 text-marigold" /> {c}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-5 inline-flex items-center gap-1 text-marigold text-sm font-semibold group-hover:gap-2 transition-all">
                    {app.launch_label || "Launch"} <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </div>
            </Link>
          );
        })}

        <div className="rounded-3xl border-2 border-dashed border-border p-8 flex flex-col items-center justify-center text-center bg-paper/40" data-testid="apps-more">
          <div className="w-14 h-14 rounded-2xl bg-paper flex items-center justify-center mb-4">
            <AppWindow className="w-7 h-7 text-ink2" />
          </div>
          <h3 className="font-heading text-2xl font-semibold mb-1">More coming soon</h3>
          <p className="text-sm text-ink2 max-w-xs">The apps registry is designed to grow — every tool is discoverable here first.</p>
        </div>
      </div>
    </div>
  );
}
